import { useEffect, useRef } from 'react'
import useTranslation from 'next-translate/useTranslation'
import ArrowForwardIcon from '@ancon/wildcat-ui/shared/icons/arrow-forward-long.svg'
import {
  CheckoutDetails,
  CheckoutStatus,
  OrderType,
  PreOrderCheckoutDetails,
  PreOrderStatus,
} from '@ancon/wildcat-types'
import clsx from 'clsx'
import { useRouter } from 'next/router'
import omit from 'lodash/omit'

import Modal from '../../../app/components/modal/Modal'
import Button from '../../../app/components/Button'
import {
  isPreOrderCheckoutSelector,
  preOrderLinkOutletURLSlugSelector,
  preOrderSelectedListItemHostAttendeeIdSelector,
  preOrderSelectedListItemIsSharableSelector,
  preOrderSelectedOrderDetailsSelector,
  preOrderSelectedOrderFetchPendingSelector,
  preOrderSelectedPreOrderIdSelector,
  selectedPreOrderCheckoutStatusSelector,
  selectedPreOrderOrderFormatSelector,
  selectedPreOrderOrderTypeSelector,
  selectedPreOrderOutletIdSelector,
  selectedPreOrderStatusSelector,
} from '../../store/preOrderSelectors'
import useAppDispatch from '../../../../store/hooks/useAppDispatch'
import useAppSelector from '../../../../store/hooks/useAppSelector'
import {
  preOrderClearCheckout,
  preOrderSetSelectedPreOrder,
  setPreOrderUser,
} from '../../store/preOrderSlice'
import { fetchCustomerPreOrderDetails } from '../../store/preOrderThunks'
import PreOrderTotal from '../PreOrderTotal'
import OrderTotal from '../../../checkout/components/orderTotal/OrderTotal'
import PreOrderAllDetailsSkeleton from '../skeletons/PreOrderAllDetailsSkeleton'
import localStorageUtils from '../../../app/utils/localStorageUtils'
import { LocalStorageCartMeta } from '../../../checkout/types'
import useAppStore from '../../../../store/hooks/useAppStore'
import { PreOrderUser } from '../../types'
import {
  clientContextCustomerEmailSelector,
  clientContextCustomerNameSelector,
} from '../../../clientContext/store/clientContextSelectors'
import storedPreOrderUserToken from '../../utils/storedPreOrderUserToken'
import { AppRoutes } from '../../../app/constants'
import getPreOrderPageURL from '../../utils/getPreOrderPageURL'
import useIsRouteChanging from '../../../app/hooks/useIsRouteChanging'
import { clearCurrentCheckout } from '../../../checkout/store/checkoutSlice'
import { checkoutCurrentCheckoutIdSelector } from '../../../checkout/store/checkoutSelectors'

import IndividualPreOrderItemList from './IndividualPreOrderItemList'
import PreOrderDetailsModalHeader from './PreOrderDetailsModalHeader'
import PreOrderConfigurationSummary from './PreOrderConfigurationSummary'
import PreOrderAttendeesSearchList from './PreOrderAttendeesSearchList'
import styles from './PreOrderDetailsModal.module.scss'

enum ModalFooterAction {
  Primary,
  Secondary,
}

export default function PreOrderDetailsModal() {
  const { t } = useTranslation('preOrder')

  const router = useRouter()

  const store = useAppStore()

  const dispatch = useAppDispatch()

  const selectedPreOrderId = useAppSelector(preOrderSelectedPreOrderIdSelector)

  const isOrderDetailsFetchPending = useAppSelector(
    preOrderSelectedOrderFetchPendingSelector,
  )
  const preOrderDetails = useAppSelector(preOrderSelectedOrderDetailsSelector)
  const isSharablePreOrder = useAppSelector(
    preOrderSelectedListItemIsSharableSelector,
  )!
  const preOrderStatus = useAppSelector(selectedPreOrderStatusSelector)
  const preOrderType = useAppSelector(selectedPreOrderOrderTypeSelector)
  const preOrderCheckoutStatus = useAppSelector(
    selectedPreOrderCheckoutStatusSelector,
  )

  const clickedAction = useRef<ModalFooterAction | null>(null)

  // Whether host can manage pre-order or add items to it
  const canManage =
    isSharablePreOrder &&
    (preOrderStatus === PreOrderStatus.Activated ||
      preOrderStatus === PreOrderStatus.Started)

  // Whether order status page is available
  const canViewOrderStatus = preOrderCheckoutStatus > CheckoutStatus.Created

  const canCheckout =
    preOrderType === OrderType.PreOrder &&
    preOrderCheckoutStatus === CheckoutStatus.Created

  const isRouteChanging = useIsRouteChanging(() => {
    dispatch(preOrderSetSelectedPreOrder(null))
  })

  const hasPrimaryAction = canManage || canCheckout || canViewOrderStatus
  const hasSecondaryAction = canManage

  useEffect(() => {
    if (selectedPreOrderId) {
      dispatch(fetchCustomerPreOrderDetails())
    }
  }, [dispatch, selectedPreOrderId])

  function handleOnClose() {
    dispatch(preOrderSetSelectedPreOrder(null))
  }

  function createPreOrderUserTokenIfNeeded() {
    const hostAttendeeId = preOrderSelectedListItemHostAttendeeIdSelector(
      store.getState(),
    )!
    const hostEmail = clientContextCustomerEmailSelector(store.getState())!
    const hostName = clientContextCustomerNameSelector(store.getState())

    const storedAttendee = storedPreOrderUserToken.getUser()

    if (!storedAttendee || storedAttendee.id !== hostAttendeeId) {
      // Create pre-order user token associated with selected pre-order
      const preOrderUser: PreOrderUser = {
        id: hostAttendeeId,
        email: hostEmail,
        name: hostName,
        isHost: true,
      }
      storedPreOrderUserToken.create(preOrderUser)
      dispatch(setPreOrderUser(preOrderUser))
    }
  }

  function removeCurrentCheckout() {
    const isPreOrderCheckout = isPreOrderCheckoutSelector(store.getState())

    // Clear cart meta from local storage
    localStorageUtils.removeItem('cartMeta')

    // Clear checkout state in store
    if (isPreOrderCheckout) {
      dispatch(preOrderClearCheckout())
    } else {
      dispatch(clearCurrentCheckout())
    }
  }

  function storeCheckoutMetaIfNeeded() {
    const outletId = selectedPreOrderOutletIdSelector(store.getState())!

    const currentCheckoutId = checkoutCurrentCheckoutIdSelector(
      store.getState(),
    )

    if (currentCheckoutId !== selectedPreOrderId) {
      if (currentCheckoutId) {
        removeCurrentCheckout()
      }

      // Create cart in local storage
      localStorageUtils.setItem<LocalStorageCartMeta>('cartMeta', {
        outletId,
        checkoutId: selectedPreOrderId!,
        isPreOrderCheckout: isSharablePreOrder,
      })
    }

    if (isSharablePreOrder) {
      createPreOrderUserTokenIfNeeded()
    }
  }

  function handleAddItems() {
    storeCheckoutMetaIfNeeded()

    const urlSlug = preOrderLinkOutletURLSlugSelector(store.getState())
    const orderFormat = selectedPreOrderOrderFormatSelector(store.getState())

    if (urlSlug && orderFormat) {
      router.push(getPreOrderPageURL(urlSlug, orderFormat))
    }
  }

  function handleManagePreOrder() {
    storeCheckoutMetaIfNeeded()
    router.push(AppRoutes.PreOrderManage)
  }

  function handleViewOrderStatus() {
    removeCurrentCheckout()

    if (isSharablePreOrder) {
      createPreOrderUserTokenIfNeeded()
    }

    const outletId = selectedPreOrderOutletIdSelector(store.getState())!

    const orderStatusPath = isSharablePreOrder
      ? AppRoutes.PreOrderStatus
      : AppRoutes.OrderStatus

    const checkoutId = selectedPreOrderId!

    router.push(`${orderStatusPath}/${outletId}/${checkoutId}`)
  }

  function handleGoToCheckout() {
    storeCheckoutMetaIfNeeded()
    router.push(AppRoutes.Checkout)
  }

  function handlePrimaryAction() {
    clickedAction.current = ModalFooterAction.Primary

    if (canViewOrderStatus) {
      handleViewOrderStatus()
    } else if (canManage) {
      handleManagePreOrder()
    } else if (canCheckout) {
      handleGoToCheckout()
    }
  }

  function handleSecondaryAction() {
    clickedAction.current = ModalFooterAction.Secondary

    if (canManage) {
      handleAddItems()
    }
  }

  function getPrimaryActionLabel() {
    if (canViewOrderStatus) {
      return t('goToStatusPage')
    }

    if (canManage) {
      return t('manageOrder')
    }

    if (canCheckout) {
      return t('goToCheckout')
    }

    return ''
  }

  function getSecondaryActionLabel() {
    if (canManage) {
      return t('addItems')
    }

    return ''
  }

  function getPrimaryActionIcon() {
    if (canViewOrderStatus) {
      return <ArrowForwardIcon />
    }

    return null
  }

  return (
    <Modal
      CustomModalHeader={PreOrderDetailsModalHeader}
      isOpen={!!selectedPreOrderId}
      onClose={handleOnClose}
      mobileModalMode="bottom-sheet"
      contentClassName={styles.modalContent}
    >
      {isOrderDetailsFetchPending || !preOrderDetails ? (
        <PreOrderAllDetailsSkeleton isShareable={isSharablePreOrder} />
      ) : (
        <>
          <div className={clsx(styles.modalBody, styles.content)}>
            <div className={styles.left}>
              <PreOrderConfigurationSummary />
            </div>
            <div className={styles.right}>
              {isSharablePreOrder ? (
                <PreOrderAttendeesSearchList />
              ) : (
                <IndividualPreOrderItemList />
              )}
            </div>
          </div>
          <div className={clsx(styles.content, styles.modalFooter)}>
            <div className={clsx(styles.left, styles.modalFooterActions)}>
              {hasSecondaryAction && (
                <Button
                  className={styles.secondary}
                  variant="secondary"
                  size="large"
                  onClick={handleSecondaryAction}
                  loading={
                    isRouteChanging &&
                    clickedAction.current === ModalFooterAction.Secondary
                  }
                >
                  {getSecondaryActionLabel()}
                </Button>
              )}
              {hasPrimaryAction && (
                <Button
                  size="large"
                  onClick={handlePrimaryAction}
                  loading={
                    isRouteChanging &&
                    clickedAction.current === ModalFooterAction.Primary
                  }
                >
                  {getPrimaryActionLabel()}
                  {getPrimaryActionIcon()}
                </Button>
              )}
            </div>
            <div className={styles.right}>
              {isSharablePreOrder ? (
                <PreOrderTotal
                  className={styles.orderTotal}
                  checkout={
                    (canViewOrderStatus
                      ? omit(preOrderDetails, 'minimumOrderDifferenceAmount')
                      : preOrderDetails) as PreOrderCheckoutDetails
                  }
                  separateTotal
                  showTotalItemsQty
                />
              ) : (
                <OrderTotal
                  className={styles.orderTotal}
                  orderSummaryTotals={
                    (canViewOrderStatus
                      ? omit(preOrderDetails, 'minimumOrderDifferenceAmount')
                      : preOrderDetails) as CheckoutDetails
                  }
                  separateTotal
                />
              )}
            </div>
          </div>
        </>
      )}
    </Modal>
  )
}
